/**
 * Journey Settings Panel (DC-1569)
 *
 * Per-journey configuration surface. Today: I-pillar target ontology picker
 * (Decision 7, DC-1564). Future: home for any other journey-level config
 * (domain pack selection, custom thresholds, etc.).
 *
 * The target ontology determines which private ontology Tier 2 inferred
 * evidence is evaluated against. When no target is set, I2 and I4 fall to
 * Tier 1 verified (if UC tags carry class IRIs) or Tier 3 fail. There is no
 * fallback — this is locked behavior per the design doc.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Settings as SettingsIcon, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useApiClient } from '../../hooks/useApiClient';
import { useJourneyId } from '../../contexts/JourneyContext';
import { JourneyDomainAssignment } from '../../components/triangulation/JourneyDomainAssignment';

// ============================================================================
// TYPES
// ============================================================================

interface OntologySummary {
  ontology_id: string;
  name: string;
  namespace: string;
  version: string;
  format: string;
  description?: string;
}

interface OntologiesListResponse {
  ontologies: OntologySummary[];
  total: number;
}

interface TargetResponse {
  target_ontology_id: string | null;
}

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

// ============================================================================
// COMPONENT
// ============================================================================

export const JourneySettingsPanel: React.FC = () => {
  const journeyId = useJourneyId();
  const { authFetch } = useApiClient();

  const [ontologies, setOntologies] = useState<OntologySummary[]>([]);
  const [currentTargetId, setCurrentTargetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ kind: 'idle' });

  const loadInitialState = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);

      const [ontologiesRes, targetRes] = await Promise.all([
        authFetch('/api/ontologies?limit=200'),
        authFetch(`/api/journey/${journeyId}/i-pillar-target-ontology`),
      ]);

      if (!ontologiesRes.ok) {
        throw new Error(`Failed to load ontologies: ${ontologiesRes.statusText}`);
      }
      if (!targetRes.ok) {
        throw new Error(`Failed to load current target: ${targetRes.statusText}`);
      }

      const ontologiesData = (await ontologiesRes.json()) as OntologiesListResponse;
      const targetData = (await targetRes.json()) as TargetResponse;

      setOntologies(ontologiesData.ontologies ?? []);
      setCurrentTargetId(targetData.target_ontology_id);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load journey settings');
    } finally {
      setLoading(false);
    }
  }, [authFetch, journeyId]);

  useEffect(() => {
    loadInitialState();
  }, [loadInitialState]);

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const raw = event.target.value;
      const nextTargetId = raw === '' ? null : raw;

      setSaveState({ kind: 'saving' });
      try {
        const res = await authFetch(
          `/api/journey/${journeyId}/i-pillar-target-ontology`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ target_ontology_id: nextTargetId }),
          },
        );

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({ error: 'Save failed' }));
          throw new Error(errBody.error || `Save failed: ${res.statusText}`);
        }

        const data = (await res.json()) as TargetResponse;
        setCurrentTargetId(data.target_ontology_id);
        setSaveState({ kind: 'saved' });
        // Auto-clear the "saved" indicator after 2s so it doesn't linger
        setTimeout(() => {
          setSaveState((prev) => (prev.kind === 'saved' ? { kind: 'idle' } : prev));
        }, 2000);
      } catch (err) {
        setSaveState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to save target ontology',
        });
      }
    },
    [authFetch, journeyId],
  );

  return (
    <div className="journey-settings-panel" style={panelStyle}>
      <header style={headerStyle}>
        <SettingsIcon size={20} aria-hidden="true" />
        <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Journey settings</h2>
      </header>

      <section aria-labelledby="i-pillar-target-heading" style={sectionStyle}>
        <h3 id="i-pillar-target-heading" style={sectionHeadingStyle}>
          I-pillar target ontology
        </h3>
        <p style={helpTextStyle}>
          Sets which uploaded ontology FAIR Tier 2 (inferred) scoring evaluates I2 and I4 against.
          {' '}
          <strong>Without a target set, I2 and I4 score at Tier 1 verified (if columns carry UC tag IRIs) or Tier 3 fail.</strong>
          {' '}
          There is no fallback — the inferred-evidence path only fires when a target is selected here.
        </p>

        {loading && (
          <div style={statusRowStyle} role="status" aria-live="polite">
            <Loader2 size={16} className="spin" aria-hidden="true" />
            <span>Loading available ontologies…</span>
          </div>
        )}

        {loadError && (
          <div style={errorRowStyle} role="alert">
            <AlertCircle size={16} aria-hidden="true" />
            <span>{loadError}</span>
            <button
              type="button"
              onClick={loadInitialState}
              style={retryButtonStyle}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !loadError && (
          <>
            <label htmlFor="i-pillar-target-select" style={labelStyle}>
              Target ontology
            </label>
            <select
              id="i-pillar-target-select"
              value={currentTargetId ?? ''}
              onChange={handleChange}
              disabled={saveState.kind === 'saving'}
              style={selectStyle}
              aria-describedby="i-pillar-target-help"
            >
              <option value="">— No target set —</option>
              {ontologies.map((ont) => (
                <option key={ont.ontology_id} value={ont.ontology_id}>
                  {ont.name} (v{ont.version})
                </option>
              ))}
            </select>

            {ontologies.length === 0 && (
              <p id="i-pillar-target-help" style={emptyStateStyle}>
                {/* DC-1645: Private Ontologies left Settings — the rail's Ontology destination is the access point. */}
                No ontologies uploaded yet. Upload one under Ontology (left rail) to enable Tier 2 inferred scoring.
              </p>
            )}

            {saveState.kind === 'saving' && (
              <div style={statusRowStyle} role="status" aria-live="polite">
                <Loader2 size={16} className="spin" aria-hidden="true" />
                <span>Saving…</span>
              </div>
            )}

            {saveState.kind === 'saved' && (
              <div style={savedRowStyle} role="status" aria-live="polite">
                <Check size={16} aria-hidden="true" />
                <span>Saved</span>
              </div>
            )}

            {saveState.kind === 'error' && (
              <div style={errorRowStyle} role="alert">
                <AlertCircle size={16} aria-hidden="true" />
                <span>{saveState.message}</span>
              </div>
            )}
          </>
        )}
      </section>

      {/* DC-1617 — reassign/clear the triangulation domain of an existing journey. */}
      <JourneyDomainAssignment journeyId={journeyId} />
    </div>
  );
};

// ============================================================================
// STYLES — inline to avoid pulling in a new CSS file for a v1.2 surface that
// may move during the v1.2 UX overhaul with Larissa
// ============================================================================

const panelStyle: React.CSSProperties = {
  padding: '1.5rem',
  maxWidth: '720px',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginBottom: '1.5rem',
};

const sectionStyle: React.CSSProperties = {
  border: '1px solid var(--line, #1c2a54)',
  borderRadius: '8px',
  padding: '1.25rem',
  background: 'var(--pane, #0f1730)',
};

const sectionHeadingStyle: React.CSSProperties = {
  margin: '0 0 0.5rem 0',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--text, #e8eefc)',
};

const helpTextStyle: React.CSSProperties = {
  margin: '0 0 1rem 0',
  fontSize: '0.875rem',
  color: 'var(--text-secondary, #b8c5dc)',
  lineHeight: 1.5,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500,
  marginBottom: '0.375rem',
  color: 'var(--text, #e8eefc)',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  fontSize: '0.875rem',
  border: '1px solid var(--line, #1c2a54)',
  borderRadius: '6px',
  background: 'var(--pane, #0f1730)',
  color: 'var(--text, #e8eefc)',
  colorScheme: 'dark',
};

const emptyStateStyle: React.CSSProperties = {
  margin: '0.75rem 0 0 0',
  fontSize: '0.8125rem',
  color: 'var(--muted, #8ea1c7)',
  fontStyle: 'italic',
};

const statusRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: '0.75rem',
  fontSize: '0.875rem',
  color: 'var(--text-secondary, #b8c5dc)',
};

const savedRowStyle: React.CSSProperties = {
  ...statusRowStyle,
  color: 'var(--success-text, #68d391)',
};

const errorRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: '0.75rem',
  fontSize: '0.875rem',
  color: 'var(--error, #e74c3c)',
};

const retryButtonStyle: React.CSSProperties = {
  marginLeft: 'auto',
  padding: '0.25rem 0.625rem',
  fontSize: '0.8125rem',
  border: '1px solid currentColor',
  borderRadius: '4px',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
};
